import { useEffect, useState } from "react";
import api from "@/lib/api";
import PromptModal from "@/components/PromptModal";
import { X, Route, Clock, MapPin, User, Fuel } from "lucide-react";
import { toast } from "sonner";
import { fmtXPF, fuelReason, fuelKey, fuelCostHint } from "@/lib/fuelCost";

const fmtT = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { timeZone: "Pacific/Noumea", hour: "2-digit", minute: "2-digit" }) : "—");
const dur = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}` : `${m} min`);

export default function TripsModal({ vehicle, onClose, onSelectTrip }) {
  const [d, setD] = useState(null);
  const [addr, setAddr] = useState({});
  const [fuel, setFuel] = useState(null); // { map: {key->cell}, price_basis } | null | 'error'
  const [promptTrip, setPromptTrip] = useState(null);
  const saveAsTemplate = async (name, t) => {
    try {
      await api.post("/route-templates/from-trip", { name, vehicle_id: vehicle.id, points: (t.points || []).map((p) => ({ lat: p.lat, lon: p.lon })), duration_min: t.duration_min || 0, corridor_m: 150 });
      toast.success("Trajet type créé depuis ce trajet réel");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur (droits admin ?)"); }
  };
  useEffect(() => { api.get(`/vehicles/${vehicle.id}/trips`).then((r) => setD(r.data)).catch(() => setD({ count: 0, trips: [], total_km: 0 })); }, [vehicle.id]);
  // Coût carburant par trajet (lecture seule, même segmentation que /trips). Un seul appel véhicule/jour.
  useEffect(() => {
    let cancel = false;
    setFuel(null);
    api.get(`/vehicles/${vehicle.id}/trip-fuel-cost`).then((r) => {
      if (cancel) return;
      const map = {};
      (r.data?.trips || []).forEach((t) => { map[fuelKey(vehicle.id, t.start)] = t; });
      setFuel({ map, price_basis: r.data?.price_basis });
    }).catch(() => { if (!cancel) setFuel("error"); });
    return () => { cancel = true; };
  }, [vehicle.id]);
  useEffect(() => {
    if (!d?.trips?.length) return;
    let cancel = false;
    (async () => {
      for (let i = 0; i < d.trips.length; i++) {
        const t = d.trips[i];
        if (!t.points?.length) continue;
        const s = t.points[0], e = t.points[t.points.length - 1];
        try {
          const rs = await api.get(`/geocode/reverse?lat=${s.lat}&lon=${s.lon}`);
          const re = await api.get(`/geocode/reverse?lat=${e.lat}&lon=${e.lon}`);
          if (!cancel) setAddr((a) => ({ ...a, [i]: { start: rs.data.address, end: re.data.address } }));
        } catch { /* ignore */ }
      }
    })();
    return () => { cancel = true; };
  }, [d]);
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="glass rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 animate-slidein" onClick={(e) => e.stopPropagation()} data-testid="trips-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="label-mono">Trajets du jour — {vehicle.name}</p>
            <p className="font-bold text-lg">{d ? `${d.count} trajet(s) · ${d.total_km} km` : "…"}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        {d && d.trips.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Aucun trajet enregistré aujourd'hui.</p>}
        {d && d.trips.length > 0 && <p className="text-[11px] text-muted-foreground mb-2">Cliquez un trajet pour l'afficher sur la carte.</p>}
        {d && d.trips.length > 0 && fuel && fuel !== "error" && fuel.price_basis === "current" && (
          <p className="text-[10px] text-muted-foreground mb-2 flex items-start gap-1" data-testid="trips-fuel-price-note">
            <Fuel className="w-3 h-3 mt-0.5 shrink-0 brand-orange" />
            <span>Coût = litres CAN × <strong>prix actuel</strong> configuré (pas le prix au moment du trajet).</span>
          </p>
        )}
        <div className="space-y-2">
          {d && d.trips.map((t, i) => (
            <button key={i} type="button" onClick={() => onSelectTrip && onSelectTrip(t)}
              className="w-full text-left border border-border rounded-lg p-3 bg-secondary/40 hover:border-[#17A2B8] hover:bg-[#17A2B8]/10 transition-colors" data-testid={`trip-${i}`}>
              {t.gap_before_min != null && <p className="text-[11px] text-muted-foreground flex items-center gap-1 mb-1.5"><Clock className="w-3 h-3" /> Arrêt {dur(t.gap_before_min)} avant ce trajet</p>}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-semibold text-sm"><Route className="w-4 h-4 brand-teal" /> {fmtT(t.start)} → {fmtT(t.end)}</div>
                <div className="data text-sm font-bold brand-orange">{t.distance_km} km</div>
              </div>
              <p className="data text-xs text-muted-foreground mt-1">Durée : {dur(t.duration_min)} · {t.points?.length || 0} points</p>
              {fuel && fuel !== "error" && (() => {
                const cell = fuel.map[fuelKey(vehicle.id, t.start)];
                if (!cell) return null;
                if (cell.cost_xpf != null) {
                  return (
                    <p className="flex items-center gap-1 text-[11px] font-bold brand-orange mt-1" data-testid={`trip-fuel-${i}`} title={fuelCostHint(cell)}>
                      <Fuel className="w-3 h-3" /> {fmtXPF(cell.cost_xpf)}
                    </p>
                  );
                }
                const r = fuelReason(cell.unavailable_reason);
                return (
                  <p className="flex items-center gap-1 text-[11px] text-muted-foreground mt-1" data-testid={`trip-fuel-${i}`} title={r.hint}>
                    <Fuel className="w-3 h-3" /> Coût : {r.label}
                  </p>
                );
              })()}
              {t.driver_name && <p className="flex items-center gap-1 text-[11px] font-semibold text-[#17A2B8] mt-1" data-testid={`trip-driver-${i}`}><User className="w-3 h-3" /> {t.driver_name}</p>}
              <div className="mt-1.5 space-y-0.5">
                <p className="flex items-start gap-1 text-[11px]"><MapPin className="w-3 h-3 mt-0.5 shrink-0 text-[#10B981]" /><span className="truncate">{addr[i]?.start || "Adresse départ…"}</span></p>
                <p className="flex items-start gap-1 text-[11px]"><MapPin className="w-3 h-3 mt-0.5 shrink-0 text-[#EF4444]" /><span className="truncate">{addr[i]?.end || "Adresse arrivée…"}</span></p>
              </div>
              <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setPromptTrip(t); }} data-testid={`trip-astemplate-${i}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#17A2B8] hover:underline cursor-pointer"><Route className="w-3 h-3" /> Définir comme trajet type</span>
            </button>
          ))}
        </div>
      </div>
      {promptTrip && (
        <PromptModal title="Nommer le trajet type" label="Nom du trajet type"
          defaultValue={`${vehicle.name} — ${fmtT(promptTrip.start)}→${fmtT(promptTrip.end)}`}
          placeholder="Ex: Dépôt → Chantier Nord" confirmLabel="Créer"
          onConfirm={(name) => { saveAsTemplate(name, promptTrip); setPromptTrip(null); }}
          onClose={() => setPromptTrip(null)} />
      )}
    </div>
  );
}
